import Button from "@/components/Button"
import TextInput from "@/components/forms/TextInput"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { ErrorMessage, Field, Form, Formik } from "formik"
import * as yup from "yup"
import { Checkbox } from "@nextui-org/checkbox"
import { useSerialDevice } from "@/contexts/serial-device"
import { MODE_HEADPHONES, MODE_RCA } from "@/constants"
import { toast } from "react-toastify"
import { ReactComponent as CheckIcon } from "@/icons/check-icon.svg"
import { ReactComponent as InfoIcon } from "@/icons/info-icon.svg"
import { useEffect, useState } from "react"
import { axios } from "@/contexts/auth"
import { Tooltip } from "@nextui-org/react"
import HeadphoneAutocomplete from "@/components/forms/HeadphoneAutocomplete"
import HeadphoneBrandAutocomplete from "@/components/forms/HeadphoneBrandAutocomplete"

type Props = {
  onConfirm: (values: {
    name: string
    mode: string
    config: object
    headphone?: {
      id: number
      brand: {
        id: number
        name: string
      }
      model: string
    } | null
  }) => void
  mode: string
  isOpen: boolean
  onClose: () => void
  refreshDeviceProfiles: () => Promise<void>
  deviceSupportsProfiles?: boolean
}

type HeadphoneBrand = {
  id: number
  name: string
}

type Headphone = {
  id: number
  brand: HeadphoneBrand
  model: string
}

const NewPresetModal = ({
  onConfirm,
  mode,
  isOpen,
  onClose,
  refreshDeviceProfiles,
  deviceSupportsProfiles = false,
}: Props) => {
  const { saveProfile } = useSerialDevice()
  const [brands, setBrands] = useState<HeadphoneBrand[]>([])
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null)
  const [headphoneModel, setHeadphoneModel] = useState<string>("")
  const [selectedHeadphone, setSelectedHeadphone] = useState<Headphone | null>(null)

  // Reset form values when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedBrandId(null)
      setHeadphoneModel("")
      setSelectedHeadphone(null)
    }
  }, [isOpen])

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get("/api/equalizer/headphone-brands/")
        setBrands(response.data)
      } catch (error) {
        console.error("Error fetching headphone brands:", error)
      }
    }
    fetchBrands()
  }, [])

  const handleCreateNewHeadphone = async (model: string) => {
    if (!selectedBrandId) throw new Error("No brand selected")

    const response = await axios.post("/api/equalizer/headphones/create/", {
      brand_id: selectedBrandId,
      model: model,
    })
    return response.data
  }

  const handleSubmit = async (values) => {
    try {
      // If save to device is checked, save the profile to device first
      if (values.execute_save_profile) {
        // Convert our mode format to device format (e.g. "HEADPHONES" -> "Headphone")
        const deviceModeMap = {
          [MODE_HEADPHONES]: "Headphone",
          [MODE_RCA]: "RCA",
        }
        const deviceMode = deviceModeMap[mode]

        const response = await saveProfile(values.name, deviceMode)
        if (!response.Status) {
          throw new Error("Failed to save profile to device")
        }

        // Refresh device profiles list after successful save
        await refreshDeviceProfiles()
      }

      // Create the preset object with the updated values
      const newPreset = {
        name: values.name,
        mode: mode,
        config: {}, // This should be passed from the parent component
        is_shared: values.is_shared,
        headphone_id: values.headphone_id,
        // If headphone_id is null, don't include the headphone object
        headphone: values.headphone_id ? selectedHeadphone : null,
      }

      // Then proceed with the normal onConfirm callback
      onConfirm(newPreset)
    } catch (error) {
      console.error("Error saving profile:", error)
      toast.error("Failed to save profile to device")
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="overflow-visible">
        {(onClose) => (
          <div className="relative">
            <Formik
              initialValues={{
                name: "",
                execute_save_profile: false,
                headphone_id: null,
                is_shared: false,
              }}
              validationSchema={yup.object({
                name: yup
                  .string()
                  .min(3)
                  .max(100)
                  .required("Name is required")
                  .test(
                    "device-compatible",
                    "Allowed characters: A-Z, 0-9, space, and -_.,!?()&$#@%*+=<>:;'\"\\/",
                    function (value) {
                      // Only apply character restrictions if saving to device
                      if (!this.parent.execute_save_profile) return true
                      return /^[A-Za-z0-9\s\-_.,!?()&$#@%*+=<>:;'"\/\\]+$/.test(value)
                    }
                  )
                  .test(
                    "device-length",
                    "Name must be 16 characters or less when saving to device",
                    function (value) {
                      // Only apply length restriction if saving to device
                      if (!this.parent.execute_save_profile) return true
                      return value.length <= 16
                    }
                  ),
                headphone_id: yup
                  .number()
                  .nullable()
                  .test(
                    "headphone-required",
                    "You have selected a Headphone Brand. Please select a Headphone Model.",
                    function (value) {
                      // If no brand is selected, any model selection (or none) is valid
                      if (!selectedBrandId) return true
                      // If brand is selected, model must also be selected
                      return value !== null
                    }
                  ),
              })}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, values, setFieldValue, isValid, dirty }) => (
                <Form>
                  <ModalBody>
                    <h2 className="type-body-xl font-bold text-white">Save As New Preset</h2>
                    <div className="relative mt-4">
                      <Field
                        as={TextInput}
                        theme="modal"
                        name="name"
                        type="text"
                        autoFocus={true}
                        placeholder="Type preset name"
                        isInvalid={errors.name && touched.name}
                      />
                      <ErrorMessage
                        component="div"
                        className="type-body-xs absolute -top-2 right-0 -translate-y-full text-right italic text-red"
                        name="name"
                      />
                    </div>
                    <div className="mt-4">
                      {deviceSupportsProfiles && (
                        <div className="flex items-center">
                          <Checkbox
                            isSelected={values.execute_save_profile}
                            onValueChange={(value) => setFieldValue("execute_save_profile", value)}
                            classNames={{
                              wrapper: [
                                "bg-cod-gray border border-earth rounded-plus before:border-0 after:bg-transparent text-red mr-2 w-4.5 h-4.5",
                                "group-data-[selected=true]:bg-eclipse group-data-[selected=true]:border-darker-red",
                              ],
                              label: "font-bulo text-[13px] text-ghost",
                              icon: "transition-opacity duration-150 w-3",
                            }}
                            icon={<CheckIcon />}
                            disableAnimation={true}
                          >
                            Also Save to Element IV?
                          </Checkbox>
                          <Tooltip
                            content={
                              "You may save unlimited presets to your Core account. You may store up to 10 presets directly on your Element IV for easy access when listening via consoles/tablets/etc."
                            }
                            placement="bottom"
                            closeDelay={0}
                            classNames={{
                              content:
                                "max-w-[400px] px-5 py-2 bg-red rounded-md type-body-sm bg-tundora text-silver",
                            }}
                          >
                            <span className="ml-1.5 cursor-help">
                              <InfoIcon className="h-3 w-3 text-silver" />
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                    <div className="mt-6">
                      <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
                        <span className="inline-block pt-0.75">Tag a Headphone (optional)</span>
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-x-4">
                        <div>
                          <div className="type-body-xs mb-2 text-silver">Brand</div>
                          <HeadphoneBrandAutocomplete
                            brands={brands}
                            value={selectedBrandId}
                            onChange={(brandId) => {
                              setSelectedBrandId(brandId)
                              // Clear headphone selection when brand changes
                              setFieldValue("headphone_id", null)
                              setHeadphoneModel("")
                              setSelectedHeadphone(null)
                            }}
                          />
                        </div>
                        <div>
                          <div className="type-body-xs mb-2 text-silver">Model</div>
                          <HeadphoneAutocomplete
                            selectedBrandId={selectedBrandId}
                            value={headphoneModel}
                            onChange={(value) => {
                              setHeadphoneModel(value)
                              // If input is cleared, ensure headphone_id is cleared and validation is triggered
                              if (!value.trim()) {
                                setSelectedHeadphone(null)
                                setFieldValue("headphone_id", null, true)
                              }
                            }}
                            onSelect={(headphone) => {
                              setSelectedHeadphone(headphone)
                              setFieldValue("headphone_id", headphone?.id || null, true)
                            }}
                            onCreateNew={handleCreateNewHeadphone}
                            isInvalid={!!(errors.headphone_id && touched.headphone_id)}
                            disabled={!selectedBrandId}
                          />
                          <ErrorMessage
                            component="div"
                            className="type-body-xs mt-1 text-right italic text-red"
                            name="headphone_id"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Checkbox
                        isSelected={values.is_shared}
                        onValueChange={(value) => setFieldValue("is_shared", value)}
                        classNames={{
                          wrapper: [
                            "bg-cod-gray border border-earth rounded-plus before:border-0 after:bg-transparent text-red mr-2 w-4.5 h-4.5",
                            "group-data-[selected=true]:bg-eclipse group-data-[selected=true]:border-darker-red",
                          ],
                          label: "font-bulo text-[13px] text-ghost",
                          icon: "transition-opacity duration-150 w-3",
                        }}
                        icon={<CheckIcon />}
                        disableAnimation={true}
                      >
                        Share this preset with the community
                      </Checkbox>
                    </div>
                  </ModalBody>
                  <ModalFooter className="gap-x-0">
                    <Button
                      theme="blue"
                      size="sm"
                      type="submit"
                      outline={true}
                      solid={false}
                      className=""
                      full={false}
                      disabled={!isValid || !dirty}
                    >
                      Save Preset
                    </Button>
                    <Button
                      theme="none"
                      size="sm"
                      className="-mr-5"
                      onPress={onClose}
                      outline={false}
                      solid={true}
                      full={false}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}

export default NewPresetModal
